import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const curYear = new Date().getFullYear();
const defaultJournalFrom = (d) => {
  if (!d) return "";
  return Number(d.slice(0, 4)) >= curYear ? d.slice(0, 7) : `${curYear}-01`;
};
const blank = () => ({ name: "", code: "", category: "kelompok_1", method: "straight_line", acquisition_date: new Date().toISOString().slice(0, 10), cost: "", residual_value: "", useful_life_months: "", journal_from: "", disposal_date: "", location: "", notes: "", source_transaction_id: "" });

export function AssetForm({ clientId, categories, editAsset, fromTxn, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank());
  useEffect(() => {
    if (!open) return;
    if (editAsset) setF({ ...blank(), ...editAsset });
    else if (fromTxn) setF({ ...blank(), name: fromTxn.description || fromTxn.vendor || "", acquisition_date: fromTxn.date, cost: fromTxn.amount, source_transaction_id: fromTxn.id, journal_from: defaultJournalFrom(fromTxn.date), notes: [fromTxn.vendor, fromTxn.invoice_no].filter(Boolean).join(" · ") });
    else setF({ ...blank(), journal_from: defaultJournalFrom(new Date().toISOString().slice(0, 10)) });
  }, [open, editAsset, fromTxn]);
  const cat = categories.find((c) => c.value === f.category) || {};
  const noDeclining = cat.db === null || cat.db === undefined && f.category !== "custom";

  const save = async () => {
    const payload = { client_id: clientId, name: f.name, code: f.code || "", category: f.category, method: noDeclining ? "straight_line" : f.method,
      acquisition_date: f.acquisition_date, cost: Number(f.cost || 0), residual_value: Number(f.residual_value || 0),
      useful_life_months: Number(f.useful_life_months || 0), journal_from: f.journal_from || "", disposal_date: f.disposal_date || "",
      location: f.location || "", notes: f.notes || "", source_transaction_id: f.source_transaction_id || "" };
    try {
      if (editAsset) await api.put(`/fixed-assets/${editAsset.id}`, payload);
      else await api.post("/fixed-assets", payload);
      toast.success("Aset disimpan & jurnal penyusutan diperbarui"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const inp = (k, label, type = "text", hint) => (
    <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
      <Input type={type} className={type === "number" ? "num" : ""} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`asset-${k.replace(/_/g, "-")}`} />
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}</div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-asset-btn"><Plus className="h-4 w-4 mr-2" />Tambah Aset</Button>}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{editAsset ? "Edit Aset Tetap" : "Tambah Aset Tetap"}</DialogTitle>
          <DialogDescription className="text-xs">Penyusutan dimulai pada bulan perolehan. Jurnal: Beban Penyusutan (D) vs Akumulasi Penyusutan (K).</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{inp("name", "Nama Aset")}</div>
          {inp("code", "Kode Aset (kosong = otomatis)")}
          {inp("location", "Lokasi / PIC")}
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Kelompok / Kategori Umur</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="asset-category"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            {cat.hint && <p className="text-[10px] text-muted-foreground">Contoh: {cat.hint}</p>}
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Metode</Label>
            <Select value={noDeclining ? "straight_line" : f.method} onValueChange={(v) => setF({ ...f, method: v })} disabled={noDeclining}>
              <SelectTrigger data-testid="asset-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="straight_line">Garis Lurus{cat.sl ? ` (${cat.sl}%/th)` : ""}</SelectItem>
                <SelectItem value="declining">Saldo Menurun{cat.db ? ` (${cat.db}%/th)` : ""}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.category === "custom" ? inp("useful_life_months", "Masa Manfaat (bulan)", "number") : <div />}
          {inp("acquisition_date", "Tanggal Perolehan", "date")}
          {inp("cost", "Harga Perolehan (Rp)", "number")}
          {inp("residual_value", "Nilai Sisa (opsional)", "number", "Fiskal umumnya 0")}
          {inp("journal_from", "Mulai Dijurnal (YYYY-MM)", "month", "Penyusutan sebelum bulan ini dianggap sudah ada di saldo awal")}
          {inp("disposal_date", "Tanggal Dijual / Dilepas (opsional)", "date")}
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Catatan</Label>
            <Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="asset-notes" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="asset-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
